<script>
import Layout from "@/router/layouts/main";
import Breadcrumb from "@/components/Breadcrumb";
import { api } from "@/state/services";
import { VclList } from "vue-content-loading";

// Tipos e tamanho aceitos no anexo (mesma regra da API)
const ALLOWED_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "doc", "docx", "xls", "xlsx"];
const MAX_SIZE = 10 * 1024 * 1024;

export default {
  components: {
    Layout,
    Breadcrumb,
    VclList,
  },
  data() {
    return {
      loading: true,
      errored: false,
      notFound: false,
      ticket: null,
      reply: {
        loading: false,
        description: "",
        file: null,
        fileError: "",
      },
    };
  },
  computed: {
    badgeStyle() {
      const color = this.ticket && this.ticket.ticket_status.color ? this.ticket.ticket_status.color : "#74788d";
      return { backgroundColor: color, color: "#fff" };
    },
  },
  watch: {
    "$route.params.id"() {
      this.getTicket();
    },
  },
  methods: {
    getTicket() {
      this.loading = true;
      this.errored = false;
      this.notFound = false;

      api
        .get("ticket/" + this.$route.params.id)
        .then((response) => {
          if (response.data.status == "success") {
            this.ticket = response.data.ticket;
          } else {
            this.notFound = true;
          }
        })
        .catch(() => {
          this.errored = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    checkFile(file) {
      this.reply.fileError = "";
      if (!file) {
        return true;
      }

      const extension = String(file.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(extension)) {
        this.reply.fileError = "Use PDF, JPG, PNG, DOC, DOCX, XLS ou XLSX.";
        return false;
      }
      if (file.size > MAX_SIZE) {
        this.reply.fileError = "O arquivo deve ter até 10 MB.";
        return false;
      }
      return true;
    },
    submitReply() {
      if (this.reply.description.trim() === "" || !this.checkFile(this.reply.file)) {
        return;
      }

      const formData = new FormData();
      formData.append("description", this.reply.description);
      if (this.reply.file) {
        formData.append("file", this.reply.file);
      }

      this.reply.loading = true;
      api
        .post("ticket/" + this.ticket.id, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((response) => {
          if (response.data.status == "success") {
            this.reply.description = "";
            this.reply.file = null;
            this.$toast.success(response.data.message);
            // Recarrega para atualizar status e linha do tempo
            this.getTicket();
          } else {
            this.$toast.error(response.data.message || "Não foi possível enviar a mensagem, tente novamente.");
          }
        })
        .catch(() => {
          this.$toast.error("Não foi possível enviar a mensagem, tente novamente.");
        })
        .finally(() => {
          this.reply.loading = false;
        });
    },
  },
  mounted() {
    this.getTicket();
  },
};
</script>

<template>
  <Layout>
    <Breadcrumb :items="[{ text: 'Ajuda' }, { text: 'Fale Conosco', to: '/contact' }, { text: ticket ? ticket.protocol : '' }]" />
    <div class="row">
      <div class="col-12">
        <div class="page-title-box d-flex justify-content-between align-items-center">
          <div v-if="ticket">
            <h3 class="mb-0 notranslate">{{ ticket.protocol }}</h3>
            <small class="text-muted">{{ ticket.category.name || "—" }} · {{ ticket.subject }}</small>
          </div>
          <h3 v-else class="mb-0">Fale Conosco</h3>
          <div class="d-flex align-items-center">
            <span v-if="ticket" class="badge badge-pill font-size-12 mr-2" :style="badgeStyle">{{ ticket.ticket_status.name }}</span>
            <router-link to="/contact" class="btn btn-default btn-sm">Voltar</router-link>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading && !ticket" class="card">
      <div class="card-body"><vcl-list class="col-lg-6"></vcl-list></div>
    </div>
    <div v-else-if="errored" class="card">
      <div class="card-body text-center py-4">
        <p>Não foi possível carregar a solicitação.</p>
        <b-button variant="default" size="sm" @click="getTicket">Tentar novamente</b-button>
      </div>
    </div>
    <div v-else-if="notFound" class="card">
      <div class="card-body text-center py-4">
        <p>Solicitação não encontrada.</p>
        <router-link to="/contact" class="btn btn-default btn-sm">Voltar para Fale Conosco</router-link>
      </div>
    </div>
    <div v-else-if="ticket" class="row">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title mb-3">Conversa</h5>
            <div v-if="ticket.waiting" class="alert alert-warning">
              <i class="bx bx-error-circle mr-1"></i> A equipe precisa de uma informação sua. Responda abaixo.
            </div>

            <div
              v-for="(row, index) in ticket.interactions"
              :key="index"
              class="d-flex mb-3"
              :class="row.type === 'affiliate' ? 'justify-content-start' : 'justify-content-end'"
            >
              <div class="contact-bubble p-3 rounded" :class="row.type === 'affiliate' ? 'bg-light' : 'contact-bubble-team'">
                <div class="d-flex justify-content-between mb-1">
                  <strong class="mr-3">{{ row.type === "affiliate" ? "Você" : "Equipe" }}</strong>
                  <small class="text-muted notranslate">{{ row.date }}</small>
                </div>
                <div class="contact-text" v-html="row.description"></div>
                <a v-if="row.upload.file" :href="row.upload.file" target="_blank" class="d-inline-block mt-2">
                  <i class="bx bx-paperclip"></i> {{ row.upload.name || "Anexo" }}
                </a>
              </div>
            </div>

            <div v-if="ticket.closed" class="alert alert-secondary mb-0">Esta solicitação está encerrada.</div>
            <b-form v-else class="mt-4" @submit.prevent="submitReply">
              <b-form-group label="Resposta" label-for="contact-reply">
                <b-form-textarea id="contact-reply" v-model="reply.description" rows="4"></b-form-textarea>
              </b-form-group>
              <b-form-group description="Opcional. Um arquivo: PDF, JPG, PNG, DOC, DOCX, XLS ou XLSX (até 10 MB).">
                <b-form-file
                  v-model="reply.file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  placeholder="Anexar arquivo"
                  browse-text="Procurar"
                  :state="reply.fileError ? false : null"
                  @input="checkFile"
                ></b-form-file>
                <div v-if="reply.fileError" class="text-danger small mt-1">{{ reply.fileError }}</div>
              </b-form-group>
              <div class="text-right">
                <b-button type="submit" variant="default" :disabled="reply.loading || reply.description.trim() === ''">
                  Enviar
                  <b-spinner v-if="reply.loading" small class="ml-2 align-middle" role="status"></b-spinner>
                </b-button>
              </div>
            </b-form>
          </div>
        </div>
      </div>

      <div class="col-lg-4">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title mb-3">Andamento</h5>
            <ul class="list-unstyled contact-timeline mb-0">
              <li v-for="(row, index) in ticket.statuses" :key="index" class="d-flex align-items-start mb-3">
                <span class="contact-dot mr-2" :style="{ backgroundColor: row.color || '#74788d' }"></span>
                <div>
                  <div>{{ row.name }}</div>
                  <small class="text-muted notranslate">{{ row.date }}</small>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.contact-bubble {
  max-width: 85%;
}
.contact-bubble-team {
  background: #e8f2fd;
}
.contact-text {
  white-space: normal;
  word-break: break-word;
}
.contact-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
}
</style>
